// v1.0.0 - 2026-02-02 - Editable cell component for Energy Data Browser

import { useState, useRef, useEffect, useCallback } from 'react'
import { cn } from '@/lib/utils/cn'

interface EditableCellProps {
  value: number
  onSave: (newValue: number) => Promise<void>
  isEditable?: boolean
  className?: string
}

export function EditableCell({
  value,
  onSave,
  isEditable = true,
  className,
}: EditableCellProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(String(value))
  const [isSaving, setIsSaving] = useState(false)
  const [hasBeenEdited, setHasBeenEdited] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Sync editValue when value prop changes
  useEffect(() => {
    if (!isEditing) {
      setEditValue(String(value))
    }
  }, [value, isEditing])

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleClick = useCallback(() => {
    if (isEditable && !isEditing && !isSaving) {
      setIsEditing(true)
    }
  }, [isEditable, isEditing, isSaving])

  const handleSave = useCallback(async () => {
    const newValue = parseFloat(editValue)
    if (isNaN(newValue) || newValue < 0) {
      setEditValue(String(value))
      setIsEditing(false)
      return
    }

    if (newValue === value) {
      setIsEditing(false)
      return
    }

    setIsSaving(true)
    try {
      await onSave(newValue)
      setHasBeenEdited(true)
      setIsEditing(false)
    } catch {
      setEditValue(String(value))
    } finally {
      setIsSaving(false)
    }
  }, [editValue, value, onSave])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleSave()
      } else if (e.key === 'Escape') {
        setEditValue(String(value))
        setIsEditing(false)
      }
    },
    [handleSave, value]
  )

  const handleBlur = useCallback(() => {
    handleSave()
  }, [handleSave])

  // Display value formatter
  const displayValue = new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(value)

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="number"
        step="0.001"
        min="0"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        disabled={isSaving}
        className={cn(
          'w-full px-2 py-1 text-right text-sm font-mono',
          'border rounded focus:outline-none focus:ring-2 focus:ring-blue-500',
          'bg-white dark:bg-gray-800',
          isSaving && 'opacity-50 cursor-wait',
          className
        )}
      />
    )
  }

  return (
    <span
      onClick={handleClick}
      className={cn(
        'block w-full px-2 py-1 text-right text-sm font-mono',
        'transition-colors duration-150',
        isEditable && 'cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded',
        hasBeenEdited && 'bg-amber-50 dark:bg-amber-900/20',
        isSaving && 'opacity-50',
        className
      )}
      title={isEditable ? 'Clicca per modificare' : undefined}
    >
      {isSaving ? '...' : displayValue}
    </span>
  )
}
